import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    Input,
    OnDestroy,
    OnInit,
    Renderer2,
    ViewChild,
    ViewEncapsulation,
    inject
} from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { NavigationEnd, Router } from '@angular/router';
import { LocalStorageService } from '@df/browser/storage/local-storage.service';
import { Meta } from '@df/core/meta/meta';
import { DfEventService } from '@df/core/service/df-event';
import { Events } from '@df/core/service/event';
import { DfImageDirective } from '@df/module-image/df-image.directive';
import { LoqateEmailValidateDirective } from '@df/module-loqate/email/loqate-email.validator';
import { RatioOptions, TextStyleOptions } from '@df/ui/atomic/atomic-options';
import { BodyClassService } from '@df/ui/body-class.service';
import { VisibilityService } from '@df/ui/common/visibility.service';
import { ValidatePatternDirective } from '@df/ui/form/validate-pattern.directive';
import { DfCmsModal } from '@df/ui/modal/df-cms-modal.decorator';
import { NDfModal } from '@df/ui/modal/df-modal.interface';
import { DfModalService } from '@df/ui/modal/df-modal.service';
import { ModalRef } from '@df/ui/modal/modal-ref';
import { TranslatePipe } from '@df/ui/translation/translate.pipe';
import { ScrollService } from '@df/util/service/scroll.service';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { skip } from 'rxjs/internal/operators/skip';
import { CMS_COMPONENT_CWV_CONFIG } from '../../../catalog/config/component.config';
import { AbstractCmsBlockComponent } from '../../../cms/block/abstract-cms-block.component';
import { NLoginFormModal } from '../../../login/form/modal/login-form-modal.interface';
import { InputWrapBodyDirective } from '../../../ui/form/input-wrap-body.directive';
import { InputWrapComponent } from '../../../ui/form/input-wrap.component';
import { SignUpPopupBigComponent } from '../sign-up-popup-big/sign-up-popup-big.component';
import { SignUpPopupInitiatorService } from './sign-up-popup-initiator.service';
import { EInitLogic, ISignUpPopupComponentData } from './sign-up-popup.interfaces';

export const SIGN_UP_POPUP_NAME = 'sign-up-popup';
export const SIGN_UP_POPUP_BODY_CLASS = 'is-sign-up-popup';
export const SIGN_UP_POPUP_OPEN_BODY_CLASS = 'sign-up-popup-open';

declare module '@df/core/service/df-event' {
    interface IEventType {
        signUpPopupClose: DfEventConfig<'signUpPopupClose', {}, void>;
    }
}

export const SIGN_UP_POPUP_CLOSE_EVENT_NAME = 'signUpPopupClose';

@DfCmsModal({
    id: 'sign-up-popup-content',
    position: 'custom',
    name: 'Sign-up popup',
    config: {
        width: '100%',
        maxWidth: undefined,
        height: '100%',
        hasBackdrop: false,
        closeOnNavigation: false,
        modalClass: 'm-r m-r-0-s m-l m-l-0-s p-b p-b-0-s sign-up-popup-modal',
        panelClass: 'sign-up-popup-panel',
        transformOptions: 'translateY(100%)',
        scrollStrategy: 'noop'
    },
    blockGroup: 'modals',
    priority: 10,
    reopenPolicy: NDfModal.ReopenPolicy.first,
    sizeRestriction: ['S', 'M', 'L', 'X'],
    allowInCms: true,
    initiators: [SignUpPopupInitiatorService],
    forbiddenLocation: /^\/(login|checkout)$/
})
@ICmsComponent<SignUpPopupContentComponent>({
    id: 'sign-up-popup-content',
    block: {
        url: 'sign-up-popup-content'
    },
    name: 'Sign-up popup',
    onDemandOnly: true,
    fields: [
        {
            key: 'appearance',
            type: NICmsContentField.EFieldType.label,
            title: 'Appearance'
        },
        <NICmsContentField.IChoiceField>{
            key: 'init_logic',
            type: NICmsContentField.EFieldType.choice,
            title: 'Init logic',
            options: [
                { key: EInitLogic.immediate, title: 'Immediate' },
                { key: EInitLogic.delayed, title: 'After time' },
                { key: EInitLogic.clicks, title: 'After number or page views' }
            ],
            required: true,
            default: EInitLogic.immediate
        },
        {
            key: 'init_clicks',
            type: NICmsContentField.EFieldType.number,
            title: 'Number of page views to show after',
            required: true,
            default: 3,
            canShow: (data: ISignUpPopupComponentData) => data.init_logic === EInitLogic.clicks
        },
        {
            key: 'init_delay',
            type: NICmsContentField.EFieldType.number,
            title: 'Number of seconds show after',
            required: true,
            default: 20,
            canShow: (data: ISignUpPopupComponentData) => data.init_logic === EInitLogic.delayed
        },
        {
            key: 'init_customer_groups',
            type: NICmsContentField.EFieldType.text,
            title: 'Coma separated group ids',
            required: false
        },
        {
            key: 'show_email_fields',
            type: NICmsContentField.EFieldType.toggle,
            title: 'Show email fields',
            default: false
        },
        {
            key: 'content',
            type: NICmsContentField.EFieldType.label,
            title: 'Content'
        },
        {
            key: 'is_big_popup',
            type: NICmsContentField.EFieldType.toggle,
            title: 'Big layout',
            default: false
        },
        <NICmsContentField.IChoiceField>{
            key: 'position',
            type: NICmsContentField.EFieldType.choice,
            title: 'Popup position',
            options: [
                { key: 'bottom', title: 'Bottom' },
                { key: 'center', title: 'Center' }
            ],
            canShow: (data: ISignUpPopupComponentData) => !data.is_big_popup
        },
        {
            type: NICmsContentField.EFieldType.text,
            key: 'popup_heading',
            title: 'Popup heading',
            default: 'Lorem ipsum',
            canShow: (data: ISignUpPopupComponentData) => !data.is_big_popup
        },
        {
            type: NICmsContentField.EFieldType.image,
            key: 'icon',
            title: 'Icon',
            canShow: (data: ISignUpPopupComponentData) => !data.is_big_popup
        },
        {
            type: NICmsContentField.EFieldType.label,
            key: 'big_popup_features',
            title: 'Big popup features',
            canShow: (data: ISignUpPopupComponentData) => data.is_big_popup
        },
        <NICmsContentField.IChoiceField>{
            key: 'big_popup_heading_style',
            type: NICmsContentField.EFieldType.choice,
            title: 'Heading Style',
            options: TextStyleOptions,
            canShow: (data: ISignUpPopupComponentData) => data.is_big_popup
        },
        {
            type: NICmsContentField.EFieldType.text,
            key: 'big_popup_main_heading',
            title: 'Main Popup heading',
            default: 'Lorem ipsum',
            canShow: (data: ISignUpPopupComponentData) => data.is_big_popup
        },
        {
            type: NICmsContentField.EFieldType.text,
            key: 'big_popup_login_heading',
            title: 'Log in heading',
            default: 'Lorem ipsum',
            canShow: (data: ISignUpPopupComponentData) => data.is_big_popup
        },
        {
            type: NICmsContentField.EFieldType.text,
            key: 'big_popup_register_heading',
            title: 'Register heading',
            default: 'Lorem ipsum',
            canShow: (data: ISignUpPopupComponentData) => data.is_big_popup
        },
        {
            type: NICmsContentField.EFieldType.text,
            key: 'big_popup_login_subtitle',
            title: 'Log in subtitle',
            default: 'dolor sit amet',
            canShow: (data: ISignUpPopupComponentData) => data.is_big_popup
        },
        {
            type: NICmsContentField.EFieldType.text,
            key: 'big_popup_register_subtitle',
            title: 'Register subtitle',
            default: 'dolor sit amet',
            canShow: (data: ISignUpPopupComponentData) => data.is_big_popup
        },
        {
            type: NICmsContentField.EFieldType.image,
            key: 'big_popup_image',
            title: 'Image',
            default: '',
            canShow: (data: ISignUpPopupComponentData) => data.is_big_popup
        },
        <NICmsContentField.IChoiceField>{
            type: NICmsContentField.EFieldType.choice,
            key: 'bigPopupImageRatio',
            title: 'Image Ratio',
            options: RatioOptions,
            default: 'ratio-4-5',
            canShow: (data: ISignUpPopupComponentData) => data.is_big_popup
        },
        <NICmsContentField.IChoiceField>{
            type: NICmsContentField.EFieldType.choice,
            key: 'bigPopupCloseColor',
            title: 'Close icon colour',
            options: [
                { key: 'col-b', title: 'Black' },
                { key: 'col-w', title: 'White' }
            ],
            default: 'col-b',
            canShow: (data: ISignUpPopupComponentData) => data.is_big_popup
        },
        ...CMS_COMPONENT_CWV_CONFIG
    ]
})
@Component({
    selector: 'sign-up-popup-content',
    templateUrl: 'sign-up-popup-content.html',
    styleUrl: './sign-up-popup-content.scss',
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [
        CommonModule,
        DfImageDirective,
        FormsModule,
        ICmsClientModule,
        InputWrapBodyDirective,
        InputWrapComponent,
        LoqateEmailValidateDirective,
        SignUpPopupBigComponent,
        TranslatePipe,
        ValidatePatternDirective
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SignUpPopupContentComponent extends AbstractCmsBlockComponent implements NICmsContentComponent.IComponent, OnInit, OnDestroy {
    @ICmsContent({
        type: 'blockContent'
    })
    override content!: ISignUpPopupComponentData;

    /**
     * The height at which the page starts
     * @type {number}
     */
    @Input()
    top?: number;

    @ViewChild('emailInputRef', { static: false })
    emailInputRef!: ElementRef<HTMLInputElement>;

    private _openMode = false;
    private _display = false;
    private _unlock?: () => void;

    updateThrottled!: () => void;
    private toggleSwitch = false;
    /**
     * Customer email
     */
    email?: string;
    /**
     * Modal configuration meta data
     * injected when modal is open
     */
    modalConfig?: NDfModal.IModalConfig;

    protected localStorageService = inject(LocalStorageService);
    protected bodyClassService = inject(BodyClassService);
    protected meta = inject(Meta);
    protected renderer = inject(Renderer2);
    protected router = inject(Router);
    protected visibilityService = inject(VisibilityService);
    protected events = inject(Events);
    protected scrollService = inject(ScrollService);
    protected dfModalService = inject(DfModalService);
    protected dfEventService = inject(DfEventService);
    protected modalRef = inject(ModalRef<SignUpPopupContentComponent>, { optional: true });

    override ngOnInit() {
        this.updateThrottled = this.timeoutService.throttle(() => this.update(), 100);
        this.subscriptions.add(this.visibilityService.isInView(this.elementRef.nativeElement).subscribe(() => this.updateThrottled()));
        this.subscriptions.add(
            this.meta.routerEvents.subscribe(routerEvent => {
                if (routerEvent instanceof NavigationEnd) {
                    this.updateThrottled();
                }
            })
        );
        this.subscriptions.add(
            this.dfEventService.success(event => {
                if (['nexusCustomerRegister', 'nexustCustomerLogin', 'nexustCheckoutLogin'].includes(event.id)) {
                    this.close();
                }
            })
        );
        this.subscriptions.add(
            this.client.isCms$.pipe(skip(1)).subscribe(() => {
                this.modalRef?.close();

                if (this._unlock) {
                    this._unlock();
                    this._unlock = undefined;
                }
            })
        );
        this.subscriptions.add(
            this.dfEventService.success(event => {
                if (event.id === SIGN_UP_POPUP_CLOSE_EVENT_NAME) {
                    this.close();
                }
            })
        );

        this.updateThrottled();

        super.ngOnInit();

        this.timeoutService.setTimeout(() => this.renderer.setAttribute(this.elementRef.nativeElement, 'visible', '1'), 500);

        if (this.content?.is_big_popup) {
            this.bodyClassService.add(SIGN_UP_POPUP_BODY_CLASS, this);
            this._unlock = this.scrollService.blockScroll();
        }

        if (!this.openMode && this.content?.show_email_fields) {
            this.openMode = true;
        }
    }

    override ngOnDestroy() {
        super.ngOnDestroy();
        if (this._unlock) {
            this._unlock();
            this._unlock = undefined;
        }
        this.bodyClassService.remove(SIGN_UP_POPUP_BODY_CLASS, this);
        this.bodyClassService.remove(SIGN_UP_POPUP_OPEN_BODY_CLASS, this);
        this.subscriptions.unsubscribe();
    }

    set openMode(value: boolean) {
        this._openMode = value;
        this.bodyClassService.set(SIGN_UP_POPUP_OPEN_BODY_CLASS, this, value);
    }

    get openMode(): boolean {
        return this._openMode;
    }

    handleKeyUp(event?: KeyboardEvent, form?: NgForm) {
        if (event?.key === 'Enter') {
            this.emailInputRef?.nativeElement?.blur();

            this.submit();
        }

        if (event && form && form['submitAttempt'] && !this.email?.length) {
            form['submitAttempt'] = false;

            this.detectChanges();
        } else if (!event && this.email?.length) {
            if (form && !form['submitAttempt']) {
                form['submitAttempt'] = true;
                form.ngSubmit.emit();
            }

            this.detectChanges();
        }
    }

    signIn() {
        this.openMode = true;
        this.detectChanges();
    }

    toggle(to = !this.toggleSwitch) {
        if (this.toggleSwitch !== to) {
            this.toggleSwitch = to;
        }

        if (to) {
            this.renderer.addClass(this.elementRef.nativeElement, 'is-popup-animation');
        } else {
            this.renderer.removeClass(this.elementRef.nativeElement, 'is-popup-animation');
        }
    }

    override update() {
        const windowPosition = !(this.client.getRootElement().scrollTop > (this.top || 0));

        this.toggle(!!windowPosition);
    }

    submit() {
        this.dfModalService.open(undefined, NLoginFormModal.LOGIN_FORM_MODAL_ID, undefined, { email: this.email });
    }

    close() {
        if (this._unlock) {
            this._unlock();
            this._unlock = undefined;
        }

        this.bodyClassService.remove(SIGN_UP_POPUP_OPEN_BODY_CLASS, this);
        this.bodyClassService.remove(SIGN_UP_POPUP_BODY_CLASS, this);

        this.dfModalService.disableSession(this.modalConfig!);

        this.localStorageService.set(SIGN_UP_POPUP_NAME, 1);

        this.modalRef?.close();
    }
}
